import { Checkbox, Divider, Flex, Typography } from 'antd';
import { useState, type ReactElement } from 'react';
import { CONTENT_PERFORMANCE_PAGE } from '@/utils/constants';
import { mixpanelTrack } from '@/utils/mixpanelUtils/asyncMixpanel';
import { MIXPANEL_EVENT, RevenueDashboardWidget } from '@/utils/mixpanelUtils/events';
import BreakdownField from '../DownloadDrawer/BreakdownField';
import DownloadDrawerShell from '../DownloadDrawer/DownloadDrawerShell';
import useCheckboxCardStyle from '../DownloadDrawer/useCheckboxCardStyle';
import useDownloadExport from '../DownloadDrawer/useDownloadExport';
import {
  BREAKDOWN_BY_PERIOD,
  buildScopedContentExportPath,
  getPeriodOptions,
  type ScopedContentExportScope,
} from '../DownloadDrawer/downloadDrawerUtils';
import useIsDownloadRangeRestricted from '@/hooks/useIsDownloadRangeRestricted';
import type {
  ContentPerformanceDownloadDrawerProps,
  ContentPerformanceGroupBy,
  ScopedContentPerformanceDownloadSettings,
} from './contentPerformanceDownloadDrawer.d';
import {
  LAUNCH_WINDOW_OPTIONS,
  buildDefaultDownloadSettings,
  getEffectiveSettings,
} from './contentPerformanceDownloadModel';

const SCOPE_BY_GROUP_BY: Record<ContentPerformanceGroupBy, ScopedContentExportScope> = {
  episode: 'episode',
  show: 'show',
  network: 'all',
};

/**
 * Drawer for the scoped content performance export: one metrics CSV, plus any
 * launch-window files the user checks. Launch windows are independent of the breakdown.
 */
const ContentPerformanceDownloadDrawer = ({
  isOpen,
  handleClose,
  selectedNetworks,
  globalFilters,
  shows,
}: ContentPerformanceDownloadDrawerProps): ReactElement | null => {
  const getCheckboxCardStyle = useCheckboxCardStyle();
  const [downloadSettings, setDownloadSettings] =
    useState<ScopedContentPerformanceDownloadSettings>(() =>
      buildDefaultDownloadSettings(globalFilters)
    );
  const isRangeRestricted = useIsDownloadRangeRestricted(downloadSettings.dateRange);
  const effectiveSettings = getEffectiveSettings(downloadSettings, isRangeRestricted);

  const hasFullNetworkAccess = selectedNetworks.every(
    (network) => network.accessLevel === 'network'
  );
  const groupByOptions: { label: string; value: ContentPerformanceGroupBy; disabled?: boolean }[] =
    [
      {
        label:
          downloadSettings.shows.length === 0 && hasFullNetworkAccess
            ? 'By network'
            : 'By all shows selected',
        value: 'network',
      },
      { label: 'By show', value: 'show' },
      { label: 'By episode', value: 'episode', disabled: isRangeRestricted },
    ];

  const launchWindowOptions = LAUNCH_WINDOW_OPTIONS.map((option) => ({
    ...option,
    style: getCheckboxCardStyle(),
  }));

  const networksWithIds = selectedNetworks.filter(
    (network): network is typeof network & { id: string } => Boolean(network.id)
  );
  const networkIds = networksWithIds.map((network) => network.id);

  const path =
    networkIds.length > 0
      ? buildScopedContentExportPath({
          networkIds,
          from: effectiveSettings.dateRange.from,
          to: effectiveSettings.dateRange.to,
          scope: SCOPE_BY_GROUP_BY[effectiveSettings.groupBy],
          breakdown: BREAKDOWN_BY_PERIOD[effectiveSettings.period],
          postLaunchComparison: effectiveSettings.launchWindows,
          showIds: effectiveSettings.shows,
        })
      : null;

  const trackingProperties = {
    networkIds,
    networkNames: networksWithIds.map((network) => network.name ?? ''),
    shows: effectiveSettings.shows.length === 0 ? 'all' : effectiveSettings.shows,
    start: effectiveSettings.dateRange.from,
    end: effectiveSettings.dateRange.to,
    includeLaunchFirst7: effectiveSettings.launchWindows.includes('7-day'),
    includeLaunchFirst30: effectiveSettings.launchWindows.includes('30-day'),
    widget: RevenueDashboardWidget.ContentPerformanceDownloadDrawer,
    page: CONTENT_PERFORMANCE_PAGE,
  };

  const { isDownloading, download } = useDownloadExport({
    path,
    onSuccess: () =>
      mixpanelTrack(
        MIXPANEL_EVENT.contentPerformance.downloadDrawer.downloadSuccess,
        trackingProperties
      ),
    onFailure: (errorCode) =>
      mixpanelTrack(MIXPANEL_EVENT.contentPerformance.downloadDrawer.downloadFailed, {
        ...trackingProperties,
        errorCode,
      }),
  });

  return (
    <DownloadDrawerShell
      isOpen={isOpen}
      onClose={handleClose}
      title="Download performance data"
      testId="content-performance-download-drawer"
      shows={shows}
      downloadSettings={downloadSettings}
      setDownloadSettings={setDownloadSettings}
      selectedFileCount={1 + downloadSettings.launchWindows.length}
      isDownloading={isDownloading}
      onDownload={download}
    >
      <Divider />
      <Typography.Title level={4} style={{ margin: 0 }}>
        Audio downloads/Video views
      </Typography.Title>
      <BreakdownField
        label="Breakdown by periods"
        options={getPeriodOptions(isRangeRestricted)}
        value={effectiveSettings.period}
        onChange={(period) => setDownloadSettings((previous) => ({ ...previous, period }))}
        style={{ marginTop: 16 }}
      />
      <BreakdownField
        label="Breakdown by Network/Show/Episode"
        options={groupByOptions}
        value={effectiveSettings.groupBy}
        onChange={(groupBy) => setDownloadSettings((previous) => ({ ...previous, groupBy }))}
        style={{ marginTop: 32 }}
      />
      <Divider />
      <Flex vertical gap={8}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          Post launch comparison for episodes
        </Typography.Title>
        <Typography.Text type="secondary">
          This will be for the shows and period selected above
        </Typography.Text>
      </Flex>
      <Checkbox.Group
        aria-label="Post launch comparison"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginTop: 16 }}
        options={launchWindowOptions}
        value={downloadSettings.launchWindows}
        onChange={(launchWindows) =>
          setDownloadSettings((previous) => ({ ...previous, launchWindows }))
        }
      />
    </DownloadDrawerShell>
  );
};

export default ContentPerformanceDownloadDrawer;
